import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import SyncIndicator from './SyncIndicator';
import { Home, Map, CreditCard, LogOut, Sprout, Settings, Menu, X, Globe, ChevronDown, Shield, Mail, Lightbulb, CloudRain } from 'lucide-react';

const Layout = ({ children, onOpenChat }) => {
  const { logout, user, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const menuItems = [
    { path: '/', icon: Home, label: t('nav.dashboard', 'Dashboard') },
    { path: '/fields', icon: Map, label: t('nav.fields', 'Fields') },
    { path: '/weather-map', icon: CloudRain, label: 'Weather Map' },
    { path: '/subscription', icon: CreditCard, label: t('nav.subscription', 'Subscription') },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];

  const navItemStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '11px 14px', borderRadius: 12,
    color: isActive ? 'var(--gold)' : 'var(--text-secondary)',
    background: isActive ? 'rgba(198,170,88,0.10)' : 'transparent',
    fontSize: 14, fontWeight: 500, cursor: 'pointer',
    transition: 'all .15s ease', textDecoration: 'none',
    border: 'none', width: '100%', textAlign: 'left',
  });

  return (
    <div className="page-shell" style={{ display: 'flex' }}>
      {/* Mobile Header */}
      {isMobile && (
        <header style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 54, background: 'var(--panel-strong)', backdropFilter: 'blur(14px)', borderBottom: '1px solid var(--border-soft)', display: 'flex', alignItems: 'center', padding: '0 14px', gap: 12, zIndex: 90 }}>
          <button style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, background: 'transparent', border: 'none', color: 'var(--text)', cursor: 'pointer', borderRadius: 10 }} onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
            <Menu size={22} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            <Sprout style={{ width: 18, height: 18, color: 'var(--gold)' }} />
            <span style={{ fontWeight: 600, color: 'var(--text)', fontSize: 15 }}>SFarm Monitor</span>
          </div>
          <SyncIndicator />
        </header>
      )}

      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 95 }} onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside style={{
        width: 250, height: '100vh', position: 'fixed', left: 0, top: 0,
        background: 'var(--panel-strong)', backdropFilter: 'blur(20px)',
        borderRight: '1px solid var(--border-soft)',
        display: 'flex', flexDirection: 'column', padding: '20px 12px',
        zIndex: 100, transition: 'transform .2s ease',
        ...(isMobile ? (sidebarOpen ? { transform: 'translateX(0)' } : { transform: 'translateX(-100%)' }) : {})
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', marginBottom: 28 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg, var(--gold), var(--gold2))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sprout style={{ width: 18, height: 18, color: '#0b241a' }} />
          </div>
          <div>
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>SFarm</span>
            <span style={{ fontSize: 16, fontWeight: 300, color: 'var(--text-secondary)', marginLeft: 4 }}>Monitor</span>
          </div>
          {isMobile && (
            <button style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }} onClick={() => setSidebarOpen(false)}>
              <X size={18} />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} data-testid={`nav-${item.label.toLowerCase()}`} style={navItemStyle(isActive)}>
                <Icon size={18} style={{ flexShrink: 0 }} />
                <span>{item.label}</span>
              </Link>
            );
          })}
          {isAdmin() && (
            <Link to="/admin" data-testid="nav-admin" style={navItemStyle(location.pathname === '/admin')}>
              <Shield size={18} style={{ flexShrink: 0 }} />
              <span>Administration</span>
            </Link>
          )}
        </nav>

        <div style={{ height: 1, background: 'var(--border-soft)', margin: '12px 0' }} />

        {/* Secondary */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <a href="mailto:office.atasagro@gmail.com" data-testid="contacts-button" style={navItemStyle(false)}>
            <Mail size={18} style={{ flexShrink: 0 }} /><span>Contacts</span>
          </a>
          <a href="mailto:office.atasagro@gmail.com?subject=App%20Suggestion" data-testid="suggestions-button" style={navItemStyle(false)}>
            <Lightbulb size={18} style={{ flexShrink: 0 }} /><span>Suggestions</span>
          </a>
        </div>

        {/* Brand CTA */}
        {onOpenChat && (
          <button onClick={() => { onOpenChat(); if (isMobile) setSidebarOpen(false); }}
            className="btn-depth-primary" data-testid="sidebar-chat-button"
            style={{ width: '100%', margin: '14px 0', padding: '12px 14px', fontSize: 14 }}>
            <Sprout size={18} /> Crop Health Scout
          </button>
        )}

        {/* Footer */}
        <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--border-soft)', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <SyncIndicator />
          <div style={{ position: 'relative' }}>
            <button onClick={() => setLangMenuOpen(!langMenuOpen)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', borderRadius: 8 }}>
              <Globe size={14} />
              <span>{i18n.language === 'bg' ? 'Български' : 'English'}</span>
              <ChevronDown size={12} style={{ marginLeft: 'auto', transition: 'transform .15s', transform: langMenuOpen ? 'rotate(180deg)' : 'none' }} />
            </button>
            {langMenuOpen && (
              <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: 'var(--panel-strong)', backdropFilter: 'blur(14px)', border: '1px solid var(--border)', borderRadius: 10, padding: 4, marginBottom: 4 }}>
                {[{ code: 'en', label: 'English' }, { code: 'bg', label: 'Български' }].map(l => (
                  <button key={l.code} onClick={() => { i18n.changeLanguage(l.code); setLangMenuOpen(false); }}
                    style={{ display: 'block', width: '100%', padding: '7px 10px', background: 'transparent', border: 'none', color: 'var(--text-secondary)', fontSize: 13, textAlign: 'left', cursor: 'pointer', borderRadius: 8 }}>
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={handleLogout} data-testid="nav-logout" style={navItemStyle(false)}>
            <LogOut size={18} style={{ flexShrink: 0 }} /><span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, marginLeft: isMobile ? 0 : 250, padding: isMobile ? '66px 14px 20px' : '24px 28px', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
        <div className="container-xl" style={{ padding: 0 }}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
